import { SUCCESS_URL } from '@/App';
import { CheckoutController } from '@/components/CheckoutController/CheckoutController';
import InfoAlert from '@/components/InfoAlert/InfoAlert';
import { useBill } from '@/hooks/useBill';
import { useBrowserUserId } from '@/hooks/useBrowserUserId';
import { useCheckBill } from '@/hooks/useCheckBill';
import useValidateQueryParams from '@/hooks/useValidateQueryParams';
import '@kitopi-com/payments/build/module/style.css';
import { useFlagsmithLoading } from '@flagsmith/flagsmith/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { dependencies } from '../../../package.json';
import styles from './Checkout.module.scss';
import { monitoring, sendBusinessEvent } from './monitoring';

const Checkout = () => {
  const flagsmithLoadingState = useFlagsmithLoading();
  const [showBillUpdatedAlert, setShowBillUpdatedAlert] = useState(false);
  const [billPaused, setBillPaused] = useState(false);
  const { isValid, queryParams } = useValidateQueryParams();
  const { outletId, tableNumber, counterNumber } = queryParams();
  const { data: bill, isError, refetch, isLoading } = useBill({ paused: billPaused });
  const browserUserId = useBrowserUserId();
  const paymentComponentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = paymentComponentRef.current;
    if (!element) return;

    const handleResize = () => {
      const isNotCheckoutSuccess = !window.location.pathname.includes(SUCCESS_URL);
      if (isNotCheckoutSuccess) {
        element.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }
    };

    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(element);

    return () => {
      resizeObserver.unobserve(element);
    };
  }, []);

  useEffect(() => {
    const isFlagsmithLoading =
      flagsmithLoadingState?.isLoading || flagsmithLoadingState?.isFetching;
    if (bill && !isFlagsmithLoading && typeof window.gtag === 'function') {
      window.gtag('event', 'web_kitopay_payment_view', {
        outlet_id: outletId || undefined,
        browserUserId,
        order_value: bill.baseTotal,
        ab_group: 1,
        service_fee_shown: true,
        qsr_service_fee_shown: true,
      });
    }
  }, [bill, browserUserId, outletId, flagsmithLoadingState]);

  // Send screen viewed business event (wait for bill)
  useEffect(() => {
    if (bill) {
      sendBusinessEvent('kitopay.screen.viewed', {
        screen: 'checkout',
        tableNumber,
        counterNumber,
        orderId: bill.orderId,
      });
    }
  }, [bill, tableNumber, counterNumber]);

  const checkBill = useCheckBill();

  // This function is used to check if the bill has been changed
  // If the bill has been changed, it will refetch the bill and show the bill updated alert
  // If the bill has not been changed, it will resolve the promise with the order has not been changed
  // If there is an error, it will resolve the promise with the order has not been changed and we try to proceed with the payment
  const recheckBill = useCallback(
    (): Promise<{ orderHasBeenChanged: boolean }> =>
      new Promise(resolve => {
        if (bill) {
          checkBill(bill.total)
            .then(({ billHasNotChanged }) => {
              if (billHasNotChanged === false) {
                refetch();
                setShowBillUpdatedAlert(true);
                setTimeout(() => {
                  setShowBillUpdatedAlert(false);
                }, 6000);
                resolve({ orderHasBeenChanged: true });
              } else {
                resolve({ orderHasBeenChanged: false });
              }
            })
            .catch(error => {
              monitoring({
                type: 'error',
                attributes: {
                  errorMessage: 'Failed to check if the bill has been changed',
                  paymentSDKVersion: dependencies['@kitopi-com/payments'],
                  debugMessage: error instanceof Error ? error.message : String(error),
                },
              });
              resolve({ orderHasBeenChanged: false });
            });
        } else {
          resolve({ orderHasBeenChanged: false });
        }
      }),
    [bill, bill?.total, checkBill, refetch]
  );

  if (!isValid())
    return (
      <div>
        Error loading table information.
        <br />
        Please scan QR code again
      </div>
    );

  return (
    <div className={styles.root}>
      {showBillUpdatedAlert && (
        <InfoAlert message="Your bill was updated, please review and proceed again" />
      )}
      <CheckoutController
        paymentComponentRef={paymentComponentRef}
        isLoading={isLoading}
        recheckBill={recheckBill}
        bill={bill}
        isBillError={isError}
        onPaymentPhaseChange={setBillPaused}
      />
    </div>
  );
};

export default Checkout;
